import React, { useCallback, useState, useMemo } from 'react';

// DEPENDENCIES
import HTMLReactParser from 'html-react-parser';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import lowerCase from 'lodash/lowerCase';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';

// GLOBAL FUNCTIONS
import { formatFunctions, modalFunctions, sleep } from 'functions.js';

// HELPERS
import useRecordView from 'helpers/getRecordView';

// GLOBAL COMPONENTS
import Record from 'parts/record/Record';
import HeaderData from '../../../tools/HeaderData';

// MAIN COMPONENT
const InventoryDetail = ({
  children,
  onEditClick,
  buttons,
  headerRight,
  setLoading,
  ...props
}) => {
  const location = useLocation();
  const anchor = location?.state?.anchor;
  const search = location?.search;
  const searchParams = useMemo(
    () => queryString.parse(search),
    [search]
  )

  const initialValues = useMemo(
    () => ({
      date_created: formatFunctions.dateTime(new Date()),
      purchase_date: formatFunctions.dateTime(new Date()),
      is_order: '0',
      purchase_method_id: 2,
      stock_num: searchParams.stock_num,
      invoice_cost_with_inventory_pack: 0,
      invoice_cost_with_repair_invoice: 0
    }),
    [searchParams]
  );

  const [record, setRecord] = useState({});

  const { isAdd, isEdit, isNew, isView } = useRecordView();

  const formatRecord = useCallback(
    ({
      date_created = new Date(),
      aryPriceAdjustmentHistory,
      aryTrackCode,
      aryActivityLog,
      aryDMSSyncLog,
      aryTrackCodeStatusHistory,
      ...record
    } = {}) => {
      isView && setLoading(false);
      return {
        ...record,
        date_created: formatFunctions.dateTime(date_created),
        dummy: {
          aryPriceAdjustmentHistory,
          aryTrackCode,
          aryActivityLog,
          aryDMSSyncLog,
          aryTrackCodeStatusHistory
        }
      };
    },
    // eslint-disable-next-line
    []
  );

  const formatParams = ({ date_created, ...v }) => ({
    ...v,
    purchase_method_id: get(v, 'purchase_method_id[0]', v.purchase_method_id),
    location_type_id: get(v, 'location_type_id[0]', v.location_type_id) || 3 // default to 3
  });

  const { stock_num, vehicle_year, make, model, inventory_status_name } = record;

  const status = useMemo(() => lowerCase(inventory_status_name).replace(/\s/g, ''), [inventory_status_name]);

  const handleSuccess = useCallback(
    ({ result }) => {
      if (isAdd) modalFunctions.success(
        `Stock# ${result.stock_num} has been added to your ${isNew ? 'new' : 'pre-owned'} inventory.`
      );
    },
    [isAdd, isNew]
  )

  const handleLoadRecord = useCallback(
    async r => {
      setRecord(r)
      if (anchor) {
        await sleep(1000);
        document.getElementById(anchor).scrollIntoView({ behavior: 'smooth' });
        history.replaceState(null, {});
      }
    },
    [anchor, setRecord]
  );

  const canEdit = useMemo(() => !isEmpty(record) && !['delivered', 'returned'].includes(status), [record, status]);

  return (
    <Record.Update
      recordIDKey="daily_sales_inventory_id"
      recordLabel="vehicle"
      endpoints={ENDPOINTS.inventory}
      onSuccess={handleSuccess}
      buttonsBefore
      buttons={buttons}
      initialValues={initialValues}
      formatRecord={formatRecord}
      formatParams={formatParams}
      onLoadRecord={handleLoadRecord}
      headerTools={[
        <HeaderData
          stockNo={stock_num}
          year={vehicle_year}
          make={make}
          model={model}
          onEditClick={canEdit && onEditClick}
        />
      ]}
      headerRight={headerRight}
      usePreloader={!isView}
      {...Object.assign(
        (isAdd || isEdit) && {
          successMessage: isEdit ? `Stock# ${stock_num} has been updated.` : ''
        },
        props
      )}
      // errorMessage={false} // TODO: Why was this false?
      errorMessage={e => {
        return e?.errors?.length ? HTMLReactParser(`<div>${e.errors.join('. ')}</div>`) : `Unable to save vehicle.`
      }}
      deleteButton={false}
      fixedHeader={isView}
    >
      {children}
    </Record.Update>
  );
};

// EXPORT
export default InventoryDetail;
